import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraDashboardPage } from '@mono/ui-components/admirals';
import { SpecraMetricStrip, SpecraOverlayDialog } from '@mono/ui-components/captains';
import { SpecraButton, SpecraIconButton, SpecraSelect, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraEmptyState, SpecraInlineBanner, SpecraPageHeader, SpecraPopover, SpecraSectionHeader, SpecraSegmentedControl, SpecraTabBar } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useIsMutating, useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { MoreHorizontal } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { pricingApiConnect } from '../../../../lib/api/pricingApi.connect';
import { projectDataClient } from '../../../../lib/connect/clients';
import { PricingEvidencePanel } from '../../PricingWorkspace/PricingEvidencePanel';
import { CompareScenariosDrawer } from './CompareScenariosDrawer';
import { CompetitiveAdjustmentDialog } from './CompetitiveAdjustmentDialog';
import { EscalationPopover } from './EscalationPopover';
import { PricingEditorsNode } from './editors/PricingEditorsNode';
import { HoursPopover } from './HoursPopover';
import { LinkDecisionDialog } from './LinkDecisionDialog';
import { PriceVolumeTab } from './PriceVolumeTab';
import { PRICING_NODE_LABELS, parsePricingParams, pricingHref } from './pricingRoutes';
import { RateRowRail } from './RateRowRail';
import { RatesTab } from './RatesTab';
import { cents, money, rangeMillions } from './ratesView';
import { ScenariosTab } from './ScenariosTab';
import { SetRateDialog } from './SetRateDialog';
import { pendingWorkbookChanges } from './usePriceVolume';
import { usePricingAutosave } from './usePricingAutosave';
import { usePricingRateBook, usePricingRateBookRow, usePricingScenarios, usePricingScope, usePricingTotals, usePricingWrite } from './usePricingRateBook';
const T = Testids.pricingWorkspace;
export function PricingWorkspacePage({ projectId }) {
    const params = useParams();
    const [search, setSearch] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const parsed = parsePricingParams(params, search);
    const scope = usePricingScope(projectId, params.scenarioId, parsed.period);
    const scenarios = usePricingScenarios(scope);
    const writing = useIsMutating({
        mutationKey: [
            'pricing-write'
        ]
    });
    const header = useQuery({
        queryKey: [
            'proposal-record-header',
            scope.principal,
            projectId
        ],
        enabled: !!scope.principal,
        staleTime: 0,
        retry: false,
        queryFn: ()=>projectDataClient.getProposalRecordHeader({
                projectId
            })
    });
    const all = location.pathname.endsWith('/scenarios');
    const scenario = scenarios.data?.scenarios.find((s)=>s.id === params.scenarioId);
    const book = usePricingRateBook({
        ...scope,
        principal: !all && scenario ? scope.principal : ''
    });
    const totals = usePricingTotals({
        ...scope,
        principal: scenario ? scope.principal : ''
    });
    const detail = useQuery({
        queryKey: [
            'pricing-detail',
            scope.principal,
            projectId,
            scope.scenarioId,
            scope.period
        ],
        enabled: !!scenario,
        staleTime: 0,
        retry: false,
        queryFn: ()=>pricingApiConnect.getPricing(projectId, scope.scenarioId)
    });
    const evidence = useQuery({
        queryKey: [
            'pricing-evidence',
            scope.principal,
            projectId,
            scope.scenarioId,
            scope.period
        ],
        enabled: !!scenario,
        staleTime: 0,
        retry: false,
        queryFn: ()=>pricingApiConnect.listEvidence(projectId, scope.scenarioId)
    });
    const project = useQuery({
        queryKey: [
            'pricing-project',
            scope.principal,
            projectId
        ],
        enabled: !!scope.principal,
        staleTime: 0,
        retry: false,
        queryFn: ()=>projectDataClient.getProject({
                projectId
            })
    });
    const rows = book.isError ? [] : book.data?.rows ?? [];
    const selected = rows.find((r)=>r.categoryId === parsed.row)?.categoryId || rows[0]?.categoryId || '';
    const row = usePricingRateBookRow({
        ...scope,
        principal: parsed.node === 'rates' && !!scenario ? scope.principal : ''
    }, selected);
    const [dialog, setDialog] = useState();
    const [comparing, setComparing] = useState(false);
    const [menu, setMenu] = useState(null);
    const [escalation, setEscalation] = useState(null);
    const [hours, setHours] = useState();
    useEffect(()=>{
        setComparing(false);
    }, [
        location.key
    ]);
    useEffect(()=>{
        setDialog(undefined);
        setMenu(null);
        setEscalation(null);
        setHours(undefined);
    }, [
        scope.scenarioId,
        scope.period,
        parsed.node
    ]);
    const freeze = usePricingWrite(()=>projectDataClient.snapshotPricingVersion({
            projectId,
            pricingId: scope.scenarioId
        }));
    const refresh = usePricingWrite(()=>projectDataClient.refreshPricingFromSolicitation({
            projectId,
            scenarioId: scope.scenarioId
        }));
    const create = usePricingWrite(()=>pricingApiConnect.createPricing(projectId, {
            scenarioName: 'Primary',
            seedFromExtractions: true
        }));
    const autosave = usePricingAutosave(scope, detail.data?.pricing);
    const frozen = (evidence.isError ? undefined : evidence.data)?.versions.filter((v)=>v.frozen).sort((a, b)=>b.versionNo - a.versionNo) ?? [];
    const latest = frozen[0];
    const pricing = detail.data?.pricing;
    const canEdit = !book.isError && book.data?.capabilities?.canEdit === true;
    const access = book.data?.capabilities?.rateBookAccess === true;
    const goto = (node, scenarioId = scope.scenarioId)=>navigate(pricingHref(projectId, scenarioId, node, {
            period: scope.period
        }));
    const failure = freeze.error || refresh.error || create.error;
    const pending = writing > 0 || autosave.status === 'pending';
    const lastSaved = Math.max(freeze.submittedAt, refresh.submittedAt, Date.parse(book.data?.scenarioUpdatedAt || '') || 0);
    if (scenarios.isLoading) return /*#__PURE__*/ _jsx(SpecraText, {
        children: "Loading pricing scenarios…"
    });
    if (scenarios.error) return /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
        tone: "danger",
        children: [
            scenarios.error.message,
            /*#__PURE__*/ _jsx(SpecraButton, {
                size: "xs",
                onClick: ()=>scenarios.refetch(),
                children: "Retry"
            })
        ]
    });
    if (!params.scenarioId && !all && scenarios.data?.scenarios.length) {
        const primary = scenarios.data.scenarios.find((s)=>s.isPrimary) || scenarios.data.scenarios[0];
        return /*#__PURE__*/ _jsx(Navigate, {
            replace: true,
            to: pricingHref(projectId, primary.id, 'rates', {
                period: parsed.period,
                row: parsed.row
            })
        });
    }
    if (params.scenarioId && !scenario) return /*#__PURE__*/ _jsx(SpecraEmptyState, {
        title: "Pricing scenario not found",
        action: /*#__PURE__*/ _jsx(SpecraButton, {
            onClick: ()=>navigate(`${pricingHref(projectId)}/scenarios`),
            children: "All scenarios"
        })
    });
    if (all) return /*#__PURE__*/ _jsxs(SpecraDashboardPage, {
        padding: "page",
        header: /*#__PURE__*/ _jsx(SpecraPageHeader, {
            title: "Pricing scenarios"
        }),
        children: [
            header.isPending && /*#__PURE__*/ _jsx(SpecraText, {
                children: "Loading scenario permissions…"
            }),
            header.isError && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                tone: "danger",
                children: [
                    "Scenario permissions unavailable.",
                    ' ',
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>header.refetch(),
                        children: "Retry"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(ScenariosTab, {
                scope: scope,
                canEdit: !header.isError && header.data?.canEditPricing === true
            })
        ]
    });
    if (!scenarios.data?.scenarios.length) return /*#__PURE__*/ _jsx(SpecraEmptyState, {
        title: "Pricing has not been set up yet",
        description: create.error ? `Not saved: ${create.error.message}` : undefined,
        action: header.data?.canEditPricing && !header.isError ? /*#__PURE__*/ _jsx(SpecraButton, {
            disabled: create.isPending,
            onClick: async ()=>{
                try {
                    const result = await create.mutateAsync();
                    goto('rates', result.pricing.id);
                } catch  {
                /* inline */ }
            },
            children: "Set up from Section B"
        }) : undefined
    });
    if (book.isLoading) return /*#__PURE__*/ _jsx(SpecraText, {
        children: "Loading rate book…"
    });
    if (book.error) return /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
        tone: "danger",
        children: [
            book.error.message,
            /*#__PURE__*/ _jsx(SpecraButton, {
                size: "xs",
                onClick: ()=>book.refetch(),
                children: "Retry"
            })
        ]
    });
    const currentPeriod = book.data?.periods.some((p)=>p.periodKey === parsed.period) ? parsed.period : book.data?.periodKey || 'base';
    if (currentPeriod !== parsed.period || params.node && !PRICING_NODE_LABELS[params.node] || !params.node && !params.clinNumber && !params.tierId || parsed.row && parsed.row !== selected) return /*#__PURE__*/ _jsx(Navigate, {
        replace: true,
        to: pricingHref(projectId, scope.scenarioId, params.clinNumber ? `clin/${params.clinNumber}` : params.tierId ? `tiers/${params.tierId}` : parsed.node, {
            period: currentPeriod,
            row: parsed.row ? selected : undefined
        })
    });
    const types = [
        ...new Set(pricing?.clinPricing?.map((c)=>c.metadata.contractType).filter(Boolean))
    ].join(' + ');
    const subtitle = `${scenario?.isPrimary ? 'Primary scenario' : `Scenario ${scenario?.scenarioName}`} · ${types ? `${types} CLINs · ` : ''}rates saved immediately, every change audited · ${latest ? `last frozen v${latest.versionNo} (${latest.createdAt?.toDate().toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short'
    })})` : 'not frozen yet'}`;
    const updateQuery = (name, value)=>{
        const q = new URLSearchParams(search);
        q.set(name, value);
        if (name === 'period') q.delete('row');
        setSearch(q, {
            replace: true
        });
    };
    return /*#__PURE__*/ _jsxs(SpecraDashboardPage, {
        "data-testid": T.page,
        padding: "page",
        railVariant: "record",
        railLabel: "Row details",
        header: /*#__PURE__*/ _jsx(SpecraPageHeader, {
            title: "Pricing",
            titleSize: "ui",
            description: subtitle,
            meta: /*#__PURE__*/ _jsx(SpecraText, {
                "data-testid": T.saveStatus,
                "data-loading": pending,
                "aria-live": "polite",
                size: "meta",
                tone: "muted",
                children: failure || [
                    'error',
                    'conflict',
                    'retry'
                ].includes(autosave.status) ? 'Not saved' : pending ? 'Saving…' : lastSaved && !failure ? `Saved · ${formatDistanceToNow(lastSaved, {
                    addSuffix: true
                })}` : 'Saved'
            }),
            actions: /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs(SpecraSelect, {
                        "aria-label": "Scenario",
                        size: "sm",
                        "data-testid": T.scenarioSelect,
                        value: scope.scenarioId,
                        onChange: (e)=>e.target.value === 'all' ? navigate(`${pricingHref(projectId)}/scenarios`) : goto(parsed.node, e.target.value),
                        children: [
                            scenarios.data?.scenarios.map((s)=>/*#__PURE__*/ _jsxs("option", {
                                    value: s.id,
                                    children: [
                                        "Scenario: ",
                                        s.scenarioName,
                                        s.isPrimary ? ' · primary' : ''
                                    ]
                                }, s.id)),
                            /*#__PURE__*/ _jsx("option", {
                                value: "all",
                                children: "All scenarios…"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        "data-testid": T.compare,
                        disabled: !scope.principal || book.isError || !book.data,
                        onClick: ()=>setComparing(true),
                        children: "Compare scenarios"
                    }),
                    canEdit && /*#__PURE__*/ _jsxs(SpecraButton, {
                        "data-testid": T.freeze,
                        disabled: !totals.data || totals.isError || evidence.isError,
                        onClick: ()=>setDialog('freeze'),
                        children: [
                            "Freeze version v",
                            (latest?.versionNo ?? 0) + 1
                        ]
                    }),
                    canEdit && /*#__PURE__*/ _jsx(SpecraIconButton, {
                        label: "More pricing actions",
                        onClick: (e)=>setMenu(e.currentTarget),
                        children: /*#__PURE__*/ _jsx(MoreHorizontal, {})
                    })
                ]
            })
        }),
        rail: parsed.node === 'rates' ? row.error ? /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
            tone: "danger",
            children: [
                row.error.message,
                /*#__PURE__*/ _jsx(SpecraButton, {
                    onClick: ()=>row.refetch(),
                    children: "Retry"
                })
            ]
        }) : row.isLoading ? /*#__PURE__*/ _jsx(SpecraText, {
            children: "Loading row details…"
        }) : row.data ? /*#__PURE__*/ _jsx(RateRowRail, {
            detail: row.data,
            vehicleName: project.data?.project?.proposalInfo?.contractVehicle || '',
            canEdit: canEdit,
            access: access,
            onSetRate: (source)=>setDialog(source || 'suggestion'),
            onDecide: ()=>setDialog('decision'),
            onAdjustment: book.data?.capabilities?.canSetAdjustment ? ()=>setDialog('adjustment-cell') : undefined
        }) : undefined : undefined,
        children: [
            failure && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                tone: "danger",
                children: [
                    "Not saved: ",
                    failure.message,
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "xs",
                        onClick: ()=>{
                            if (freeze.error) freeze.mutate();
                            else if (refresh.error) refresh.mutate();
                        },
                        children: "Retry"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraTabBar, {
                label: "Pricing sections",
                value: parsed.node,
                onValueChange: goto,
                options: [
                    'overview',
                    'rates',
                    'indirects',
                    'clins',
                    ...pricing?.sharedTierConfigs?.length ? [
                        'tiers'
                    ] : [],
                    'versions',
                    'price-volume'
                ].map((value)=>({
                        value,
                        label: PRICING_NODE_LABELS[value],
                        count: value === 'rates' ? rows.length : value === 'clins' ? pricing?.clinPricing?.length : value === 'versions' ? frozen.length : undefined,
                        testId: T.tab
                    }))
            }),
            autosave.status === 'conflict' && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                tone: "warning",
                "data-testid": T.conflict,
                children: [
                    "Someone else changed pricing. Reload to see their change.",
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: autosave.reload,
                        children: "Reload"
                    }),
                    autosave.error && /*#__PURE__*/ _jsx(SpecraText, {
                        children: autosave.error
                    })
                ]
            }),
            autosave.status === 'retry' && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                tone: "warning",
                children: [
                    "Pricing reloaded. Your local edits are kept for one explicit retry.",
                    autosave.canRetryConflict && /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": T.retryEdits,
                        onClick: autosave.retry,
                        children: "Retry local edits"
                    })
                ]
            }),
            autosave.status === 'error' && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                tone: "danger",
                children: [
                    "Not saved: ",
                    autosave.error,
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: autosave.retry,
                        children: "Retry"
                    })
                ]
            }),
            !canEdit && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "neutral",
                children: "You can view pricing. Editing needs write access on this proposal, Growth, or Pricing authority."
            }),
            !access && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "info",
                children: "Rate book columns are hidden for you by your organisation's access rules (ratebook.columns.read; default: Registries access). You can still enter rates."
            }),
            parsed.node === 'rates' ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        title: "Rate card · Section B labor categories, pre-filled from the rate book"
                    }),
                    /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                        size: "sm",
                        label: "Period",
                        testId: T.period,
                        value: scope.period,
                        onValueChange: (v)=>updateQuery('period', v),
                        options: book.data?.periods.map((p)=>({
                                value: p.periodKey,
                                label: p.label
                            })) ?? []
                    }),
                    rows.some((r)=>r.removedFromSectionB) && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                        tone: "warning",
                        children: [
                            rows.filter((r)=>r.removedFromSectionB).length,
                            " labor categories were removed by an amendment. Their rates are kept and excluded from totals."
                        ]
                    }),
                    rows.some((r)=>r.link?.needsDecision) && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                        tone: "warning",
                        children: [
                            rows.filter((r)=>r.link?.needsDecision).length,
                            " labor categories need a pricing decision.",
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "xs",
                                onClick: ()=>{
                                    const first = rows.find((r)=>r.link?.needsDecision);
                                    if (first) {
                                        updateQuery('row', first.categoryId);
                                        document.querySelector(`[data-testid="${T.row}"][data-row-id="${CSS.escape(first.categoryId)}"]`)?.scrollIntoView({
                                            block: 'nearest'
                                        });
                                    }
                                },
                                children: "Show"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(RatesTab, {
                        workbookChanges: pendingWorkbookChanges(evidence.isError ? [] : evidence.data?.proposals),
                        onReviewWorkbook: ()=>goto('price-volume'),
                        rows: rows,
                        selected: selected,
                        access: access,
                        canEdit: canEdit,
                        onSelect: (id)=>updateQuery('row', id),
                        onSetRate: (r)=>{
                            updateQuery('row', r.categoryId);
                            setDialog('suggestion');
                        },
                        onHours: (r, anchor)=>setHours({
                                row: r,
                                anchor
                            })
                    }),
                    totals.error ? /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                        tone: "danger",
                        children: [
                            "Totals unavailable: ",
                            totals.error.message,
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                onClick: ()=>totals.refetch(),
                                children: "Retry"
                            })
                        ]
                    }) : /*#__PURE__*/ _jsx(SpecraMetricStrip, {
                        "data-testid": T.totals,
                        density: "compact",
                        loading: totals.isLoading,
                        metrics: [
                            {
                                id: 'labor',
                                label: 'Labor this period',
                                value: cents(totals.data?.laborByPeriod[scope.period]),
                                note: `rows with a chosen rate (${rows.filter((r)=>!!r.using?.amount && !r.removedFromSectionB).length} of ${rows.filter((r)=>!r.removedFromSectionB).length})`,
                                tone: 'neutral'
                            },
                            {
                                id: 'contract',
                                label: `Total contract value · ${latest ? `v${latest.versionNo}` : 'draft'}`,
                                value: latest ? money(latest.totalContractValue) : cents(totals.data?.totalCents),
                                note: latest ? /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        "saved on the frozen version · used at award",
                                        money(latest.totalContractValue) !== cents(totals.data?.totalCents) && /*#__PURE__*/ _jsxs(SpecraText, {
                                            as: "p",
                                            size: "meta",
                                            children: [
                                                "Current draft: ",
                                                cents(totals.data?.totalCents),
                                                " (not frozen)"
                                            ]
                                        })
                                    ]
                                }) : 'freeze a version to use it at submission and award',
                                tone: 'neutral'
                            },
                            ...(totals.data?.blackHat ?? []).map((b)=>({
                                    id: b.participantId,
                                    label: `Black hat · ${b.companyName}`,
                                    value: rangeMillions(b.lowCents, b.highCents),
                                    note: b.positionLabel,
                                    tone: 'neutral'
                                }))
                        ]
                    }),
                    evidence.error && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                        tone: "danger",
                        children: [
                            "Frozen versions unavailable: ",
                            evidence.error.message,
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                onClick: ()=>evidence.refetch(),
                                children: "Retry"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        size: "body-ui",
                        tone: "muted",
                        children: book.data?.ruleNote
                    })
                ]
            }) : parsed.node === 'versions' ? /*#__PURE__*/ _jsx(PricingEvidencePanel, {
                projectId: projectId,
                pricingId: scope.scenarioId,
                canEdit: canEdit,
                freezeDisabled: !totals.data || totals.isError || evidence.isError,
                onFreezeRequest: ()=>setDialog('freeze'),
                cacheScope: [
                    scope.principal,
                    scope.period
                ]
            }) : parsed.node === 'price-volume' ? /*#__PURE__*/ _jsx(PriceVolumeTab, {
                scope: scope,
                canEdit: canEdit,
                categories: pricing?.rateCard?.categories ?? [],
                periods: book.data?.periods ?? []
            }, `${scope.principal}:${scope.scenarioId}`) : detail.error ? /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                tone: "danger",
                children: [
                    detail.error.message,
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>detail.refetch(),
                        children: "Retry"
                    })
                ]
            }) : pricing ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                        label: "Period",
                        value: scope.period,
                        onValueChange: (v)=>updateQuery('period', v),
                        options: book.data?.periods.map((p)=>({
                                value: p.periodKey,
                                label: p.label
                            })) ?? []
                    }),
                    /*#__PURE__*/ _jsx(PricingEditorsNode, {
                        pricing: autosave.draft ?? pricing,
                        node: parsed.node,
                        clinNumber: params.clinNumber,
                        tierId: params.tierId,
                        canEdit: canEdit,
                        byClin: totals.data?.byClin,
                        serverTotal: totals.data?.totalCents,
                        onChange: autosave.edit,
                        onNavigate: goto
                    }, scope.scenarioId)
                ]
            }) : /*#__PURE__*/ _jsx(SpecraText, {
                children: "Loading scenario…"
            }),
            comparing && !!scope.principal && !book.isError && !!book.data && /*#__PURE__*/ _jsx(CompareScenariosDrawer, {
                scope: scope,
                scenarios: scenarios.data?.scenarios ?? [],
                onClose: ()=>setComparing(false)
            }, `${scope.principal}:${projectId}:${scope.scenarioId}`),
            dialog === 'freeze' && /*#__PURE__*/ _jsxs(SpecraOverlayDialog, {
                open: true,
                size: "sm",
                title: `Freeze pricing v${(latest?.versionNo ?? 0) + 1}?`,
                onOpenChange: (v)=>{
                    if (!v) setDialog(undefined);
                },
                footer: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "secondary",
                            onClick: ()=>setDialog(undefined),
                            children: "Cancel"
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            disabled: freeze.isPending || !totals.data || totals.isError || evidence.isError,
                            onClick: async ()=>{
                                try {
                                    await freeze.mutateAsync();
                                    setDialog(undefined);
                                } catch  {
                                /* page error */ }
                            },
                            children: "Freeze version"
                        })
                    ]
                }),
                children: [
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        children: [
                            "Total ",
                            cents(totals.data?.totalCents),
                            ". Frozen versions can be pinned at submission and are used at award."
                        ]
                    }),
                    freeze.error && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                        tone: "danger",
                        children: [
                            "Not saved: ",
                            freeze.error.message
                        ]
                    })
                ]
            }),
            [
                'adjusted_suggestion',
                'suggestion',
                'observation',
                'manual'
            ].includes(dialog || '') && row.data && !row.isFetching && !row.isError && canEdit && /*#__PURE__*/ _jsx(SetRateDialog, {
                scope: scope,
                detail: row.data,
                token: book.data?.scenarioUpdatedAt || '',
                access: access,
                initialSource: dialog,
                onClose: ()=>setDialog(undefined),
                onReload: ()=>Promise.all([
                        book.refetch(),
                        row.refetch()
                    ])
            }),
            [
                'adjustment',
                'adjustment-cell'
            ].includes(dialog || '') && book.data?.capabilities?.canSetAdjustment && pricing && /*#__PURE__*/ _jsx(CompetitiveAdjustmentDialog, {
                scope: scope,
                token: book.data.scenarioUpdatedAt,
                periods: book.data.periods,
                categories: pricing.rateCard?.categories ?? [],
                adjustments: pricing.rateCard?.competitiveAdjustments ?? [],
                cell: dialog === 'adjustment-cell' ? pricing.rateCard?.categories.find((c)=>c.id === selected)?.lcatLogicalKey : undefined,
                onClose: ()=>setDialog(undefined),
                onReload: ()=>Promise.all([
                        book.refetch(),
                        row.refetch(),
                        detail.refetch()
                    ])
            }),
            dialog === 'decision' && row.data?.row?.link && canEdit && /*#__PURE__*/ _jsx(LinkDecisionDialog, {
                scope: scope,
                link: row.data.row.link,
                companyId: pricing?.companyId || '',
                vehicleId: project.data?.project?.proposalInfo?.contractVehicleId || '',
                vehicleName: project.data?.project?.proposalInfo?.contractVehicle || '',
                onClose: ()=>setDialog(undefined)
            }),
            /*#__PURE__*/ _jsxs(SpecraPopover, {
                open: !!menu,
                anchor: menu,
                onOpenChange: (v)=>{
                    if (!v) setMenu(null);
                },
                "aria-label": "Pricing actions",
                children: [
                    book.data?.capabilities?.canSetAdjustment && /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "ghost",
                        "data-testid": T.adjustmentOpen,
                        onClick: ()=>{
                            setDialog('adjustment');
                            setMenu(null);
                        },
                        children: "Competitive adjustment…"
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "ghost",
                        "data-testid": T.refreshSectionB,
                        disabled: refresh.isPending,
                        onClick: ()=>{
                            refresh.mutate();
                            setMenu(null);
                        },
                        children: "Refresh from Section B"
                    }),
                    /*#__PURE__*/ _jsxs(SpecraButton, {
                        variant: "ghost",
                        onClick: ()=>{
                            setEscalation(menu);
                            setMenu(null);
                        },
                        children: [
                            "Escalation ",
                            (pricing?.rateCard?.escalation?.defaultPct ?? 0) * 100,
                            "%/yr"
                        ]
                    })
                ]
            }),
            escalation && canEdit && /*#__PURE__*/ _jsx(EscalationPopover, {
                scope: scope,
                token: book.data?.scenarioUpdatedAt || '',
                value: pricing?.rateCard?.escalation?.defaultPct ?? 0,
                anchor: escalation,
                onClose: ()=>setEscalation(null)
            }),
            hours && /*#__PURE__*/ _jsx(HoursPopover, {
                scope: scope,
                row: hours.row,
                anchor: hours.anchor,
                onClose: ()=>setHours(undefined)
            })
        ]
    });
}
